/**
 * Aggregate MoneyMojo data into report datasets
 */

import { supabase, getCurrentUserId } from '../../lib/supabase';
import { groupByDeductionTheme, buildGstSummary } from '../../lib/regions/au';
import { buildBusinessExpenseGstSummaryFromRows } from './business-expense-gst-summary';
import { buildBusinessExpenseInsights } from './business-expense-insights';
import { priorYearPeriod } from './business-expense-quarters';
import {
  enrichBusinessExpenseRow,
  type BusinessExpenseDbTransaction,
} from './business-expense-tax-enrichment';
import type {
  ReportDataset,
  ReportFilters,
  ReportFormat,
  ReportGenerationCriteria,
  ReportTransactionRow,
  ReportType,
} from '../../types/report.types';
import type { ReportDateRange } from '../../types/report.types';
import type { ServiceResponse } from '../../types/report.types';
import type { BudgetPeriodType } from '../../types/budget.types';
import type { ReportingUserContext } from '../../types/reporting-user-context';
import { getReportingDisclaimer } from './reporting-user-context';
import {
  buildBusinessExpensePrimaryCategoryGroups,
  expenseRowsFromPrimaryGroups,
} from './business-expense-dataset';
import {
  type CategoryWithTree,
  isTravelOrMotorCategoryName,
  resolveBusinessCategoryScope,
} from './business-expense-scope';
import { expandCategoryIdsToDescendants } from '../../utils/category-tree-utils';
import { formatDate } from '../../lib/regions/au';
import {
  expenseOutflowAmount,
  incomeInflowAmount,
  isExpenseOutflow,
  isIncomeInflow,
} from './transaction-amount-utils';
import {
  buildBudgetPerformanceFromTransactions,
  buildIncomeVsExpensesSection,
  buildMonthlyTrendWithComparison,
  resolvePriorComparisonPeriod,
} from './financial-summary-dataset';
import { buildFinancialSummaryInsights } from './financial-summary-insights';

interface DbTransaction extends BusinessExpenseDbTransaction {}

interface DbCategory extends CategoryWithTree {
  deductible: boolean;
  gst_applicable: boolean;
  type?: string | null;
  exclude_from_spending?: boolean;
}

interface DbAccount {
  id: string;
  name: string;
}

function applyFilters(
  transactions: DbTransaction[],
  filters?: ReportFilters,
  categories?: DbCategory[]
): DbTransaction[] {
  let result = transactions;

  if (filters?.categoryIds?.length) {
    const set = categories?.length
      ? expandCategoryIdsToDescendants(filters.categoryIds, categories)
      : new Set(filters.categoryIds);
    result = result.filter((t) => t.category_id && set.has(t.category_id));
  }
  if (filters?.accountIds?.length) {
    const set = new Set(filters.accountIds);
    result = result.filter((t) => t.account_id && set.has(t.account_id));
  }
  if (filters?.minAmount != null && filters.minAmount > 0) {
    result = result.filter((t) => Math.abs(t.amount) >= filters.minAmount!);
  }
  if (filters?.maxAmount != null && filters.maxAmount > 0) {
    result = result.filter((t) => Math.abs(t.amount) <= filters.maxAmount!);
  }
  if (filters?.keywords?.trim()) {
    const kw = filters.keywords.trim().toLowerCase();
    result = result.filter(
      (t) =>
        t.description?.toLowerCase().includes(kw) ||
        t.merchant_name?.toLowerCase().includes(kw) ||
        t.notes?.toLowerCase().includes(kw)
    );
  }
  return result;
}

function toRows(
  transactions: DbTransaction[],
  categoryMap: Map<string, DbCategory>,
  accountMap: Map<string, DbAccount>,
  options?: { enrichBusinessTax?: boolean }
): ReportTransactionRow[] {
  return transactions.map((t) => {
    const base = {
      id: t.id,
      date: t.date,
      description: t.description ?? '',
      merchant: t.merchant_name,
      amount: t.amount,
      categoryId: t.category_id,
      categoryName: t.category_id ? categoryMap.get(t.category_id)?.name ?? null : null,
      accountId: t.account_id,
      accountName: t.account_id ? accountMap.get(t.account_id)?.name ?? null : null,
    };
    if (!options?.enrichBusinessTax) return base;
    const cat = t.category_id ? categoryMap.get(t.category_id) : undefined;
    return enrichBusinessExpenseRow(t, base, cat?.gst_applicable);
  });
}

function buildExpenseByCategory(transactions: DbTransaction[], categoryMap: Map<string, DbCategory>) {
  const expenses = transactions.filter(isExpenseOutflow);
  const totals = new Map<string, { amount: number; count: number }>();
  for (const tx of expenses) {
    const name = tx.category_id ? categoryMap.get(tx.category_id)?.name ?? 'Uncategorised' : 'Uncategorised';
    const entry = totals.get(name) ?? { amount: 0, count: 0 };
    entry.amount += expenseOutflowAmount(tx);
    entry.count += 1;
    totals.set(name, entry);
  }
  const total = Array.from(totals.values()).reduce((a, b) => a + b.amount, 0) || 1;
  return Array.from(totals.entries())
    .map(([categoryName, { amount, count }]) => ({
      categoryName,
      amount,
      transactionCount: count,
      percent: (amount / total) * 100,
    }))
    .sort((a, b) => b.amount - a.amount);
}

const TRANSACTION_PAGE_SIZE = 1000;

const TRANSACTION_SELECT_CORE =
  'id, date, description, merchant_name, notes, amount, type, category_id, account_id, archived_at';
const TRANSACTION_SELECT_TAX =
  'business_use_percent, gst_status, receipt_attached';

/** Cached after first failed select when migration 036 is not applied yet. */
let transactionTaxColumnsAvailable: boolean | null = null;

function transactionSelectList(): string {
  if (transactionTaxColumnsAvailable === false) {
    return TRANSACTION_SELECT_CORE;
  }
  return `${TRANSACTION_SELECT_CORE}, ${TRANSACTION_SELECT_TAX}`;
}

function isMissingColumnError(message: string): boolean {
  return /column .+ does not exist/i.test(message);
}

const CATEGORY_SELECT =
  'id, name, type, exclude_from_spending, deductible, gst_applicable, parent_id, slug';

async function hydrateCategoryMapForTransactions(
  categoryMap: Map<string, DbCategory>,
  transactions: DbTransaction[]
): Promise<void> {
  const pending = new Set<string>();
  for (const tx of transactions) {
    if (tx.category_id && !categoryMap.has(tx.category_id)) {
      pending.add(tx.category_id);
    }
  }

  while (pending.size > 0) {
    const batch = Array.from(pending);
    pending.clear();
    const { data, error } = await supabase.from('categories').select(CATEGORY_SELECT).in('id', batch);
    if (error || !data?.length) break;
    for (const row of data as DbCategory[]) {
      categoryMap.set(row.id, row);
      if (row.parent_id && !categoryMap.has(row.parent_id)) {
        pending.add(row.parent_id);
      }
    }
  }
}

async function fetchTransactionsForPeriod(
  userId: string,
  period: ReportDateRange
): Promise<{ data: DbTransaction[] | null; error: string | null }> {
  const all: DbTransaction[] = [];
  let offset = 0;

  while (true) {
    const select = transactionSelectList();
    const { data, error } = await supabase
      .from('transactions')
      .select(select)
      .eq('user_id', userId)
      .gte('date', period.startDate)
      .lte('date', period.endDate)
      .is('archived_at', null)
      .order('date', { ascending: true })
      .order('id', { ascending: true })
      .range(offset, offset + TRANSACTION_PAGE_SIZE - 1);

    if (error) {
      if (
        transactionTaxColumnsAvailable !== false &&
        isMissingColumnError(error.message) &&
        select.includes('business_use_percent')
      ) {
        transactionTaxColumnsAvailable = false;
        offset = 0;
        all.length = 0;
        continue;
      }
      return { data: null, error: error.message };
    }
    const batch = (data ?? []) as unknown as DbTransaction[];
    if (transactionTaxColumnsAvailable === null && select.includes('business_use_percent')) {
      transactionTaxColumnsAvailable = true;
    }
    all.push(...batch);
    if (batch.length < TRANSACTION_PAGE_SIZE) break;
    offset += TRANSACTION_PAGE_SIZE;
  }

  return { data: all, error: null };
}

/** Test helper: reset tax-column availability cache. */
export function resetTransactionTaxColumnCacheForTests(): void {
  transactionTaxColumnsAvailable = null;
}

function sortTransactionsByDateAsc(transactions: DbTransaction[]): DbTransaction[] {
  return [...transactions].sort((a, b) => {
    const byDate = a.date.localeCompare(b.date);
    if (byDate !== 0) return byDate;
    return a.id.localeCompare(b.id);
  });
}

function computeCategoriesInScope(
  reportType: ReportType,
  filters: ReportFilters | undefined,
  categories: DbCategory[]
): number {
  if (reportType === 'business_expense') {
    return resolveBusinessCategoryScope(categories, filters?.categoryIds)?.size ?? 0;
  }
  if (!filters?.categoryIds?.length) {
    return 0;
  }
  return expandCategoryIdsToDescendants(filters.categoryIds, categories).size;
}

function wizardCategoriesLabel(
  reportType: ReportType,
  wizardCategoryPickCount: number,
  categoriesInScopeCount: number
): string {
  if (reportType === 'business_expense') {
    if (wizardCategoryPickCount > 0) {
      return `${categoriesInScopeCount} business categories (selected)`;
    }
    if (categoriesInScopeCount > 0) {
      return `Business categories (${categoriesInScopeCount})`;
    }
    return 'Business categories';
  }
  if (wizardCategoryPickCount === 0) {
    return 'All categories';
  }
  return `${categoriesInScopeCount} categories`;
}

function buildGenerationCriteria(
  reportType: ReportType,
  period: ReportDateRange,
  filters: ReportFilters | undefined,
  categories: DbCategory[],
  accountMap: Map<string, DbAccount>,
  title: string,
  format: ReportFormat | undefined,
  extras?: { businessScopeSummary?: string; transactionCountTotal?: number }
): ReportGenerationCriteria {
  const wizardCategoryPickCount = filters?.categoryIds?.length ?? 0;
  const categoriesInScopeCount = computeCategoriesInScope(reportType, filters, categories);
  const accountPickCount = filters?.accountIds?.length ?? 0;

  const summary = {
    title,
    dateRangeLabel: period.label,
    format: (format ?? 'pdf').toUpperCase(),
    categoriesLabel: wizardCategoriesLabel(
      reportType,
      wizardCategoryPickCount,
      categoriesInScopeCount
    ),
    categoriesInScopeCount,
    accountsLabel: accountPickCount === 0 ? 'All accounts' : `${accountPickCount} accounts`,
    accountCount: accountPickCount,
    transactionCountTotal: extras?.transactionCountTotal,
  };

  const lines: string[] = [
    `Date range (inclusive): ${formatDate(period.startDate)} – ${formatDate(period.endDate)}`,
  ];

  if (filters?.accountIds?.length) {
    const names = filters.accountIds
      .map((id) => accountMap.get(id)?.name)
      .filter((n): n is string => Boolean(n));
    if (names.length) {
      lines.push(`Account names: ${names.join(', ')}`);
    }
  }

  if (reportType === 'monthly_financial_summary') {
    lines.push(
      'Amounts: Income (credit/inflow in income category tree) and expense debits; transaction type transfer and transfer categories omitted; exclude-from-spending applies to expenses only'
    );
    lines.push('Comparison: Prior equal-length period and month-over-month deltas within the selected range');
  } else if (reportType === 'business_expense') {
    lines.push(
      extras?.businessScopeSummary ??
        'Business scope: expense outflows in Business Expenses category tree (matches Transactions list when filtered to the same categories, excluding archived)'
    );
    if (wizardCategoryPickCount === 0 && categoriesInScopeCount > 0) {
      lines.push(`Categories in scope: ${categoriesInScopeCount} under Business Expenses tree`);
    } else if (wizardCategoryPickCount > 0) {
      lines.push(`Categories in scope: ${categoriesInScopeCount} (wizard selection expanded to subcategories)`);
    }
    lines.push('Transaction status: Non-archived only (same default as Transactions)');
    lines.push('Amounts: Expense outflows (debits/expenses; transfers excluded)');
  } else {
    lines.push('Transaction status: Non-archived only');
    if (wizardCategoryPickCount > 0) {
      lines.push(`Categories in scope: ${categoriesInScopeCount} (including subcategories)`);
    }
  }

  if (filters?.minAmount != null && filters.minAmount > 0) {
    lines.push(`Minimum amount: ${filters.minAmount}`);
  }
  if (filters?.maxAmount != null && filters.maxAmount > 0) {
    lines.push(`Maximum amount: ${filters.maxAmount}`);
  }
  if (filters?.keywords?.trim()) {
    lines.push(`Keywords: "${filters.keywords.trim()}"`);
  }

  return { summary, lines, transactionCountTotal: extras?.transactionCountTotal };
}

async function loadActiveBudgetLimits(userId: string): Promise<
  Array<{ category_id: string; amount: number | string; period_type: BudgetPeriodType }>
> {
  const { data: budgets } = await supabase
    .from('budgets')
    .select('id, period_type')
    .eq('user_id', userId)
    .eq('status', 'active');

  if (!budgets?.length) return [];

  const periodByBudgetId = new Map(
    budgets.map((b) => [b.id, b.period_type as BudgetPeriodType])
  );
  const budgetIds = budgets.map((b) => b.id);
  const { data: limits } = await supabase
    .from('budget_category_limits')
    .select('budget_id, category_id, amount')
    .in('budget_id', budgetIds);

  return (limits ?? []).map((limit) => ({
    category_id: limit.category_id,
    amount: limit.amount,
    period_type: periodByBudgetId.get(limit.budget_id) ?? 'monthly',
  }));
}

async function buildBudgetPerformance(
  userId: string,
  period: ReportDateRange,
  transactions: DbTransaction[],
  categoryMap: Map<string, DbCategory>,
  categoryRecords: DbCategory[] = Array.from(categoryMap.values())
) {
  const limits = await loadActiveBudgetLimits(userId);
  if (!limits.length) return [];
  return buildBudgetPerformanceFromTransactions(
    transactions,
    limits,
    categoryMap,
    categoryRecords,
    period
  );
}

async function buildFinancialSummaryDataset(
  userId: string,
  period: ReportDateRange,
  transactions: DbTransaction[],
  categoryMap: Map<string, DbCategory>,
  includeRawData: boolean,
  accountMap: Map<string, DbAccount>
): Promise<Pick<
  ReportDataset,
  | 'incomeVsExpenses'
  | 'expenseByCategory'
  | 'budgetPerformance'
  | 'monthlyTrend'
  | 'periodComparison'
  | 'financialSummaryInsights'
  | 'rawTransactions'
>> {
  const priorPeriod = resolvePriorComparisonPeriod(period);
  const priorRes = await fetchTransactionsForPeriod(userId, {
    ...period,
    startDate: priorPeriod.startDate,
    endDate: priorPeriod.endDate,
    label: priorPeriod.label,
    preset: period.preset,
  });
  const priorTransactions = priorRes.error ? [] : priorRes.data ?? [];

  const categoryRecords = Array.from(categoryMap.values());
  const budgetLimits = await loadActiveBudgetLimits(userId);
  const summary = buildFinancialSummaryInsights({
    period,
    currentTransactions: transactions,
    priorTransactions,
    categoryMap,
    categories: categoryRecords,
    budgetLimits,
  });

  return {
    ...summary,
    rawTransactions: includeRawData ? toRows(transactions, categoryMap, accountMap) : undefined,
  };
}

function filterBusinessExpenseTransactions(
  transactions: DbTransaction[],
  categories: DbCategory[],
  filters?: ReportFilters
): { businessTransactions: DbTransaction[]; scopeMissing: boolean } {
  const scope = resolveBusinessCategoryScope(categories, filters?.categoryIds);
  if (!scope) {
    return { businessTransactions: [], scopeMissing: true };
  }
  const businessTransactions = transactions.filter(
    (t) => t.category_id && scope.has(t.category_id) && isExpenseOutflow(t)
  );
  return { businessTransactions, scopeMissing: false };
}

function buildTravelMotorVehicleSection(
  transactions: DbTransaction[],
  categoryMap: Map<string, DbCategory>
) {
  const byCategory = new Map<string, { amount: number; count: number }>();
  for (const tx of transactions) {
    if (!tx.category_id) continue;
    const name = categoryMap.get(tx.category_id)?.name ?? '';
    if (!isTravelOrMotorCategoryName(name)) continue;
    const entry = byCategory.get(name) ?? { amount: 0, count: 0 };
    entry.amount += expenseOutflowAmount(tx);
    entry.count += 1;
    byCategory.set(name, entry);
  }
  return Array.from(byCategory.entries())
    .map(([categoryName, { amount, count }]) => ({
      categoryName,
      amount,
      transactionCount: count,
    }))
    .sort((a, b) => b.amount - a.amount);
}

function buildIncomeSummary(transactions: DbTransaction[]) {
  const income = transactions.filter(isIncomeInflow);
  let paygIncome = 0;
  let interestIncome = 0;
  let dividendIncome = 0;
  let otherIncome = 0;

  for (const tx of income) {
    const amt = Math.abs(tx.amount);
    const desc = `${tx.description ?? ''} ${tx.merchant_name ?? ''}`.toLowerCase();
    if (/interest/.test(desc)) interestIncome += amt;
    else if (/dividend|franking/.test(desc)) dividendIncome += amt;
    else if (/salary|payg|wage|payroll/.test(desc)) paygIncome += amt;
    else otherIncome += amt;
  }

  return {
    paygIncome,
    interestIncome,
    dividendIncome,
    otherIncome,
    totalIncome: paygIncome + interestIncome + dividendIncome + otherIncome,
  };
}

function filtersForBusinessPrior(filters?: ReportFilters): ReportFilters | undefined {
  if (!filters?.categoryIds?.length) return filters;
  const { categoryIds: _categoryIds, ...rest } = filters;
  return rest;
}

async function assembleDataset(
  reportType: ReportType,
  period: ReportDateRange,
  title: string,
  transactions: DbTransaction[],
  categories: DbCategory[],
  categoryMap: Map<string, DbCategory>,
  accountMap: Map<string, DbAccount>,
  includeRawData: boolean,
  userId: string,
  userContext?: ReportingUserContext,
  filters?: ReportFilters,
  format?: ReportFormat
): Promise<ReportDataset> {
  const reportingMetadata = userContext
    ? {
        countryCode: userContext.countryCode,
        countryName: userContext.countryName,
        disclaimer: getReportingDisclaimer(userContext.countryCode),
      }
    : undefined;

  const base: ReportDataset = {
    reportType,
    period,
    title,
    reportingMetadata,
  };

  if (transactions.length === 0) {
    if (reportType === 'monthly_financial_summary') {
      const emptyFinancial = await buildFinancialSummaryDataset(
        userId,
        period,
        [],
        categoryMap,
        includeRawData,
        accountMap
      );
      return {
        ...base,
        emptyMessage: 'No transaction data for this period',
        ...emptyFinancial,
        incomeVsExpenses: { totalIncome: 0, totalExpenses: 0, netPosition: 0, savingsRate: null },
        monthlyTrend: [],
      };
    }
    return { ...base, emptyMessage: 'No data for this period' };
  }

  const expenseByCategory = buildExpenseByCategory(transactions, categoryMap);
  const gstSummary = buildGstSummary(transactions, categories) ?? undefined;
  const rawTransactions = includeRawData ? toRows(transactions, categoryMap, accountMap) : undefined;

  switch (reportType) {
    case 'tax_deduction':
      return {
        ...base,
        deductionThemes: groupByDeductionTheme(transactions, categories),
        gstSummary,
        rawTransactions,
      };
    case 'business_expense': {
      const { businessTransactions, scopeMissing } = filterBusinessExpenseTransactions(
        transactions,
        categories,
        filters
      );
      const businessScopeSummary = filters?.categoryIds?.length
        ? 'Business scope: wizard categories expanded to all subcategories, intersected with Business Expenses tree'
        : 'Business scope: full Business Expenses category subtree';

      if (scopeMissing) {
        return {
          ...base,
          generationCriteria: buildGenerationCriteria(
            reportType,
            period,
            filters,
            categories,
            accountMap,
            title,
            format,
            { businessScopeSummary, transactionCountTotal: 0 }
          ),
          emptyMessage:
            'No Business Expenses category found. Add a category with slug "business-expenses" or name "Business Expenses", then assign business transactions to it or its subcategories.',
        };
      }
      if (businessTransactions.length === 0) {
        return {
          ...base,
          generationCriteria: buildGenerationCriteria(
            reportType,
            period,
            filters,
            categories,
            accountMap,
            title,
            format,
            { businessScopeSummary, transactionCountTotal: 0 }
          ),
          emptyMessage: 'No business expense transactions for this period in your Business Expenses categories.',
          businessExpenseTotal: 0,
          businessExpenseTransactionCount: 0,
          expenseByCategory: [],
        };
      }
      const sortedBusiness = sortTransactionsByDateAsc(businessTransactions);
      const businessRaw = toRows(sortedBusiness, categoryMap, accountMap, {
        enrichBusinessTax: true,
      });
      const businessRowById = new Map(businessRaw.map((row) => [row.id, row]));
      const businessPrimaryGroups = buildBusinessExpensePrimaryCategoryGroups(
        sortedBusiness,
        categories,
        categoryMap,
        (tx) => businessRowById.get(tx.id)!
      );
      const businessExpenseByCategory = expenseRowsFromPrimaryGroups(businessPrimaryGroups);
      const businessTotal = sortedBusiness.reduce((s, t) => s + expenseOutflowAmount(t), 0);
      const businessTxCount = sortedBusiness.length;

      const priorPeriod = priorYearPeriod(period);
      const priorRes = userId
        ? await fetchTransactionsForPeriod(userId, priorPeriod)
        : { data: [], error: null };
      const priorFiltered = applyFilters(priorRes.data ?? [], filtersForBusinessPrior(filters), categories);
      const priorBusiness = filterBusinessExpenseTransactions(
        priorFiltered,
        categories,
        filters
      ).businessTransactions;
      const priorSorted = sortTransactionsByDateAsc(priorBusiness);
      const priorPrimaryGroups = buildBusinessExpensePrimaryCategoryGroups(
        priorSorted,
        categories,
        categoryMap,
        (tx) =>
          enrichBusinessExpenseRow(
            tx,
            {
              id: tx.id,
              date: tx.date,
              description: tx.description ?? '',
              merchant: tx.merchant_name,
              amount: tx.amount,
              categoryId: tx.category_id,
              categoryName: tx.category_id
                ? categoryMap.get(tx.category_id)?.name ?? null
                : null,
              accountId: tx.account_id,
              accountName: tx.account_id ? accountMap.get(tx.account_id)?.name ?? null : null,
            },
            tx.category_id ? categoryMap.get(tx.category_id)?.gst_applicable : undefined
          )
      );
      const priorExpenseByCategory = expenseRowsFromPrimaryGroups(priorPrimaryGroups);

      const businessExpenseInsights = buildBusinessExpenseInsights({
        period,
        transactions: businessRaw,
        expenseByCategory: businessExpenseByCategory,
        priorExpenseByCategory,
        priorPeriodHasData: priorSorted.length > 0,
        userContext,
      });
      const businessGst = businessExpenseInsights.showGstSection
        ? buildBusinessExpenseGstSummaryFromRows(businessRaw) ?? undefined
        : undefined;
      const travelMotorVehicle = buildTravelMotorVehicleSection(sortedBusiness, categoryMap);
      const generationCriteria = buildGenerationCriteria(
        reportType,
        period,
        filters,
        categories,
        accountMap,
        title,
        format,
        { businessScopeSummary, transactionCountTotal: businessTxCount }
      );

      return {
        ...base,
        generationCriteria,
        businessExpenseTotal: businessTotal,
        businessExpenseTransactionCount: businessTxCount,
        businessExpenseDetailByPrimaryCategory: businessPrimaryGroups,
        businessExpenseInsights,
        expenseByCategory: businessExpenseByCategory,
        travelMotorVehicle: travelMotorVehicle.length ? travelMotorVehicle : undefined,
        gstSummary: businessGst,
        rawTransactions: businessRaw,
      };
    }
    case 'year_end_tax_summary':
      return {
        ...base,
        incomeSummary: buildIncomeSummary(transactions),
        deductionThemes: groupByDeductionTheme(transactions, categories),
        gstSummary,
        rawTransactions,
      };
    case 'profit_and_loss': {
      const revenue = transactions.reduce((s, t) => s + incomeInflowAmount(t), 0);
      const expenses = transactions.reduce((s, t) => s + expenseOutflowAmount(t), 0);
      return {
        ...base,
        revenueTotal: revenue,
        expenseTotal: expenses,
        netProfit: revenue - expenses,
        expenseByCategory,
        rawTransactions,
      };
    }
    case 'category_spending_analysis':
      return { ...base, expenseByCategory, rawTransactions };
    case 'monthly_financial_summary': {
      const financial = await buildFinancialSummaryDataset(
        userId,
        period,
        transactions,
        categoryMap,
        includeRawData,
        accountMap
      );
      return { ...base, ...financial };
    }
    default: {
      const categoryRecords = Array.from(categoryMap.values());
      return {
        ...base,
        incomeSummary: buildIncomeSummary(transactions),
        incomeVsExpenses: buildIncomeVsExpensesSection(transactions, categoryRecords),
        expenseByCategory,
        budgetPerformance: await buildBudgetPerformance(
          userId,
          period,
          transactions,
          categoryMap,
          categoryRecords
        ),
        monthlyTrend: buildMonthlyTrendWithComparison(transactions, categoryRecords),
        rawTransactions,
      };
    }
  }
}

export async function buildDataset(
  reportType: ReportType,
  period: ReportDateRange,
  filters: ReportFilters | undefined,
  title: string,
  includeRawData: boolean,
  userId?: string,
  userContext?: ReportingUserContext,
  format?: ReportFormat
): Promise<ServiceResponse<ReportDataset>> {
  try {
    const uid = userId ?? (await getCurrentUserId());
    if (!uid) {
      return { data: null, error: 'Not authenticated' };
    }

    const [txRes, catRes, accRes] = await Promise.all([
      fetchTransactionsForPeriod(uid, period),
      supabase
        .from('categories')
        .select('id, name, type, exclude_from_spending, deductible, gst_applicable, parent_id, slug')
        .or(`user_id.eq.${uid},is_system.eq.true`),
      supabase.from('accounts').select('id, name').eq('user_id', uid),
    ]);

    if (txRes.error) return { data: null, error: txRes.error };
    if (catRes.error) return { data: null, error: catRes.error.message };
    if (accRes.error) return { data: null, error: accRes.error.message };

    let categories = (catRes.data ?? []) as DbCategory[];
    const categoryMap = new Map(categories.map((c) => [c.id, c]));
    const accountMap = new Map(((accRes.data ?? []) as DbAccount[]).map((a) => [a.id, a]));

    await hydrateCategoryMapForTransactions(categoryMap, txRes.data ?? []);
    categories = Array.from(categoryMap.values());

    // Business expense scope (including wizard category subtrees) is applied in
    // filterBusinessExpenseTransactions; skip categoryIds here so child categories match.
    const filtersForTransactions =
      reportType === 'business_expense' && filters?.categoryIds?.length
        ? { ...filters, categoryIds: undefined }
        : filters;
    const filtered = applyFilters(txRes.data ?? [], filtersForTransactions, categories);
    const effectiveRawData =
      reportType === 'business_expense' ? true : includeRawData;

    const dataset = await assembleDataset(
      reportType,
      period,
      title,
      filtered,
      categories,
      categoryMap,
      accountMap,
      effectiveRawData,
      uid,
      userContext,
      filters,
      format
    );

    const generationCriteria =
      dataset.generationCriteria ??
      buildGenerationCriteria(
        reportType,
        period,
        filters,
        categories,
        accountMap,
        title,
        format,
        {
          transactionCountTotal:
            reportType === 'business_expense'
              ? dataset.businessExpenseTransactionCount
              : dataset.rawTransactions?.length,
        }
      );

    return { data: { ...dataset, generationCriteria }, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to build dataset' };
  }
}

export const reportDataService = { buildDataset };
